import { useMemo } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  ColumnDef
} from '@tanstack/react-table';
import { Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { Typography } from '@/components/ui/typography';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { BELOW_DESKTOP_QUERY } from '@/config/ui.constants';
import { formatPriceDisplay } from '../../../../util/formatting';
import { AssetCartItem } from '../types/cart.types';
import {
  toGroupedRow,
  assetLineKey,
  type GroupedAssetRow
} from '../utils/cartTransforms';
import { AssetServiceCell, DescriptionCell } from './AssetCartCells';
import AssetCartCard from './AssetCartCard';

type AssetCartTableProps = {
  data: AssetCartItem[];
  onEdit?: (row: GroupedAssetRow) => void;
  onDelete?: (row: GroupedAssetRow) => void;
  mutatingItemIds?: Set<string>;
  readOnly?: boolean;
};

// AssetServiceCell and DescriptionCell live in AssetCartCells.tsx, shared
// with the below-lg AssetCartCard so table and card render one source of
// truth per line.

// Stable line key matching how useCart writes to mutatingItemIds (DV-2131 §3):
// prefer the top-level lineItemUUID; fall back to the (assetId, slug) composite
// using the stable singular assetId (not assetRefs[0], which can drift after a
// first-photo removal).
const isRowMutating = (row: GroupedAssetRow, mutatingItemIds: Set<string>) => {
  const lineAssetId = row.assetId ?? row.assetRefs?.[0]?.assetId ?? '';
  return mutatingItemIds.has(
    assetLineKey(
      row.lineItemUUID,
      lineAssetId,
      row.service?.shoppingCartSlug ?? '',
    ),
  );
};

const AssetCartTable = ({
  data,
  onEdit,
  onDelete,
  mutatingItemIds = new Set(),
  readOnly = false
}: AssetCartTableProps) => {
  // Below lg the lines render as cards instead of the fixed-width table —
  // same switch as DataTable's renderMobileCard slot (Unit 3 PR B).
  const isBelowLg = useMediaQuery(BELOW_DESKTOP_QUERY);
  const rows = useMemo<GroupedAssetRow[]>(() => data.map(toGroupedRow), [data]);

  const hasOrderNumbers = readOnly && data.some((item) => item.orderNumber);

  const columns = useMemo<ColumnDef<GroupedAssetRow>[]>(() => {
    const cols: ColumnDef<GroupedAssetRow>[] = [];

    if (readOnly && hasOrderNumbers) {
      cols.push({
        id: 'orderId',
        header: 'Order ID',
        size: 100,
        cell: ({ row }) => (
          <Typography variant="body-medium">
            #{row.original.orderNumber || ''}
          </Typography>
        )
      });
    }

    cols.push({
      id: 'service',
      header: 'Service',
      size: 300,
      cell: ({ row }) => <AssetServiceCell item={row.original} />
    });

    cols.push({
      id: 'description',
      header: 'Description',
      size: readOnly ? 450 : 380,
      cell: ({ row }) => (
        <DescriptionCell
          row={row.original}
          onEdit={onEdit}
          readOnly={readOnly}
        />
      )
    });

    if (!readOnly) {
      cols.push({
        id: 'qty',
        header: 'Qty',
        size: 60,
        cell: ({ row }) => (
          <Typography variant="body-medium">{row.original.quantity}</Typography>
        )
      });
    }

    cols.push({
      id: 'total',
      header: 'Total',
      size: 100,
      cell: ({ row }) => (
        <Typography variant="body-medium">
          ${formatPriceDisplay(row.original.price)}
        </Typography>
      )
    });

    if (!readOnly && onDelete) {
      cols.push({
        id: 'delete',
        header: '',
        size: 60,
        cell: ({ row }) => {
          const item = row.original;
          const label = `Remove ${
            item.service?.name || 'image edit'
          } from cart`;
          return (
            <div className="flex justify-center">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="text-muted-foreground hover:text-primary"
                aria-label={label}
                onClick={() => onDelete(item)}
              >
                <Trash2 className="h-5 w-5" />
              </Button>
            </div>
          );
        }
      });
    }

    return cols;
  }, [readOnly, hasOrderNumbers, onEdit, onDelete]);

  const table = useReactTable({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel()
  });

  if (data.length === 0) return null;

  if (isBelowLg) {
    return (
      <ul className="flex flex-col gap-3" data-testid="asset-cart-card-list">
        {rows.map((row) => (
          <li
            key={assetLineKey(
              row.lineItemUUID,
              row.assetId ?? row.assetRefs?.[0]?.assetId ?? '',
              row.service?.shoppingCartSlug ?? '',
            )}
          >
            <AssetCartCard
              row={row}
              onEdit={onEdit}
              onDelete={onDelete}
              isMutating={isRowMutating(row, mutatingItemIds)}
              readOnly={readOnly}
              showOrderNumber={hasOrderNumbers}
            />
          </li>
        ))}
      </ul>
    );
  }

  return (
    <div className="w-full overflow-x-auto">
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead
                  key={header.id}
                  className="!static !z-auto bg-muted text-xs font-medium uppercase tracking-wide text-muted-foreground"
                  style={{
                    width: header.column.getSize(),
                    minWidth: header.column.getSize()
                  }}
                >
                  {!header.isPlaceholder &&
                    flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.map((row) => {
            const isMutating = isRowMutating(row.original, mutatingItemIds);
            return (
              <TableRow
                key={row.id}
                className={
                  isMutating ? 'pointer-events-none opacity-60' : undefined
                }
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id} className="py-4 align-middle">
                    {cell.column.id === 'delete' && isMutating ? (
                      <div className="flex justify-center">
                        <Spinner size="sm" />
                      </div>
                    ) : (
                      flexRender(cell.column.columnDef.cell, cell.getContext())
                    )}
                  </TableCell>
                ))}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};

export default AssetCartTable;
